import React from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { getBest } from "./games/bestScore";
import FieldThumb from "./games/brick-breaker/FieldThumb";
import { BRICK_BREAKER } from "./games/brick-breaker/meta";
import BrickGrid from "./games/BrickGrid";
import { BRICK_DROP } from "./games/brick-drop/meta";
import { PIECE_COLORS } from "./games/brick-drop/pieces";
import { PlayIcon } from "./games/icons";
import { SKYLINE_STACKER } from "./games/skyline-stacker/meta";
import TowerThumb from "./games/skyline-stacker/TowerThumb";
import { paths } from "./routes";
import "./GamesSection.css";

// A mid-game snapshot for the card's picture (same board as the design).
const BRICK_DROP_THUMB = [
  ...Array(4).fill(".........."),
  ".....T....",
  "....TTT...",
  ...Array(6).fill(".........."),
  ".....g....",
  "....ggg...",
  "...LZZ....",
  "..LLZZJ.S.",
  "T.LOOJJJSS",
  "TTOOIIIIS.",
  "TJJLLSSZZ.",
  "OJLLLSSZZ.",
];

type GameId = "drop" | "stack" | "breaker";

interface Game {
  id: GameId;
  path: string;
  picture: React.ReactNode;
  isNew?: boolean;
}

const GAMES: Game[] = [
  {
    id: BRICK_DROP.id,
    path: paths.drop,
    picture: <BrickGrid rows={BRICK_DROP_THUMB} cell={11} colors={PIECE_COLORS} />,
  },
  {
    id: SKYLINE_STACKER.id,
    path: paths.stack,
    picture: <TowerThumb width={220} height={224} />,
  },
  {
    id: BRICK_BREAKER.id,
    path: paths.breaker,
    picture: <FieldThumb cell={13} />,
    isNew: true,
  },
];

/** "Play while you wait": the games below the landing hero. Imports no game code. */
function GamesSection() {
  const { t } = useTranslation();
  return (
    <section id="games" className="games" aria-labelledby="games-title">
      <div className="games__inner">
        <div className="games__head">
          <p className="games__eyebrow">{t("landing.playWhileYouWait")}</p>
          <h2 id="games-title" className="games__title">
            {t("landing.gamesTitle")}
          </h2>
          <p className="games__lede">{t("landing.gamesLede")}</p>
        </div>
        <div className="games__list">
          {GAMES.map((game) => {
            const best = getBest(game.id);
            const name = t(`games.${game.id}.name`);
            return (
              <article key={game.id} className="game-tile">
                <div className="game-tile__picture">
                  {game.picture}
                  {game.isNew && <span className="game-tile__new">{t("landing.newBadge")}</span>}
                </div>
                <div className="game-tile__body">
                  <h3 className="game-tile__name">{name}</h3>
                  <p className="game-tile__pitch">{t(`games.${game.id}.pitch`)}</p>
                  <div className="game-tile__chips">
                    <span className="game-tile__chip">{t(`games.${game.id}.chip`)}</span>
                    <span className="game-tile__chip">
                      {t("landing.best", { score: best > 0 ? best.toLocaleString("en-US") : "—" })}
                    </span>
                  </div>
                  <Link to={game.path} className="game-tile__play">
                    <PlayIcon /> {t("landing.play", { name })}
                  </Link>
                </div>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}

export default GamesSection;
